import { inject, Injectable } from "@angular/core";
import { SubmitInfoCourse } from "@app/core/domain/submit-proposal";
import { Course } from "@core/domain/course";
import { CurriculumCourse } from "@core/domain/curriculum";
import { CourseHelper } from "@core/domain/helpers/course-helper";
import { PlanDetails } from "@core/domain/plan-details";
import { CourseData } from "@shared/types/course-data";
import { PlanLaterType } from "@shared/types/plan-later-types";
import { PlanStateService } from "./plan-state.service";

@Injectable({
  providedIn: "root",
})
export class CourseDataMapper {
  private readonly courseHelper = inject(CourseHelper);
  private readonly planStateService = inject(PlanStateService);

  mapCourseData(
    course: Course | CurriculumCourse,
    options: {
      selectedPlan?: PlanDetails;
      isPlannable?: boolean;
      isRemovable?: boolean;
      planLaterType?: PlanLaterType;
    } = { isPlannable: false, isRemovable: true },
  ): CourseData {
    const plan = options.selectedPlan ?? this.planStateService.currentSelectedPlan();
    const plannedCourse = this.courseHelper.findPlannedCourse(course, plan);
    const isCoursePlannable = plannedCourse ? false : this.courseHelper.isCoursePlannable(course);
    const isCourseRemovable = options.isRemovable ? this.courseHelper.isRemovableFromPlanLater(course) : false;

    const courseData: CourseData = {
      ...course,
      // don't use ?? operator here, because course.code can be an empty string
      uniqueId: course.code === "" ? course.customCourseName : course.code!,
      labelColor: this.courseHelper.getLabelColor(course),
      isRibbonCourse: this.courseHelper.isRibbonCourse(course),
      isCourseObtained: this.courseHelper.isCourseObtained(course),
      isCustomCourse: this.courseHelper.isCustomCourse(course),
      isPlannable: !!options.isPlannable && isCoursePlannable,
      isPlannedCourse: !!plannedCourse,
      plannedText: plannedCourse ? this.courseHelper.getPlannedText(plannedCourse) : "",
      isRemovable: isCourseRemovable,
      isAddedToPlanLater: false, // Default false, may be updated after this
      isPartOfPlanLater: course.isInPlanLater,
      statusObtained: plannedCourse?.statusObtained ?? course.statusObtained,
      statusObtainedLabel: this.courseHelper.getStatusObtainedLabel(course),
      isEnrolled: !!plannedCourse?.isEnrolled,
      isEnrolledInPlanPeriod: plannedCourse ? this.courseHelper.isEnrolledInPlanPeriod(plannedCourse) : false,
      enrolledPeriods: plannedCourse?.enrolledPeriods ?? course.enrolledPeriods,
      planLaterType: options.planLaterType,
      isMovableToPlanLater: this.courseHelper.isMovableToPlanLater(course, plan),
      periodDescription: plannedCourse?.periodDescription ?? course.periodDescription,
      planId: plan!.id,
    };

    if (plan) {
      // Course planId may have an undefined value
      courseData.planId = course.planId > 0 ? course.planId : plan.id;
      courseData.academicYear = course.academicYear ?? (plan.startYear || 0);
    }

    return courseData;
  }

  mapSubmitInfoCourseData(submitInfoCourse: SubmitInfoCourse): CourseData {
    const plan = this.planStateService.currentSelectedPlan();
    const plannedCourse = this.courseHelper.findPlannedCourse(submitInfoCourse, plan);

    const courseData: CourseData = {
      ...submitInfoCourse,
      // don't use ?? operator here, because submitInfoCourse.code can be an empty string
      uniqueId: submitInfoCourse.code === "" ? submitInfoCourse.customCourseName : submitInfoCourse.code!,
      labelColor: this.courseHelper.getLabelColor(submitInfoCourse),
      isRibbonCourse: false,
      isCourseObtained: this.courseHelper.isCourseObtained(submitInfoCourse),
      isCustomCourse: this.courseHelper.isCustomCourse(submitInfoCourse),
      isPlannable: false,
      isPlannedCourse: !!plannedCourse,
      plannedText: plannedCourse ? this.courseHelper.getPlannedText(plannedCourse) : "",
      isRemovable: false,
      isAddedToPlanLater: submitInfoCourse.isInPlanLater,
      isPartOfPlanLater: submitInfoCourse.isInPlanLater,
      statusObtained: submitInfoCourse.statusObtained,
      statusObtainedLabel: this.courseHelper.getStatusObtainedLabel(submitInfoCourse),
      isEnrolled: !!plannedCourse?.isEnrolled,
      isEnrolledInPlanPeriod: plannedCourse ? this.courseHelper.isEnrolledInPlanPeriod(plannedCourse) : false,
      planLaterType: undefined,
      isMovableToPlanLater: false,
      enrolledPeriods: submitInfoCourse.enrolledPeriods,
      periodDescription: plannedCourse?.periodDescription ?? "",
      planId: plan!.id,
      academicYear: plan?.startYear ?? 0,
    } as CourseData;

    return courseData;
  }
}
